import React from "react";
import { DotLottieReact, type DotLottie } from "@lottiefiles/dotlottie-react";
import StyledDiv from "../../../StyledDiv/StyledDiv";
import signatureAnimationData from "../lottie/signature/signature-opti.json";
import clsx from "clsx";
import useBreakpoint from "../../../Grid/hooks/useBreakpoint";

export interface ISignatureAnimationProps {
  className?: string;
}

const MOBILE_BREAKPOINT = 375;
const ANIMATION_ASPECT_RATIO = 1920 / 1080;

const SignatureAnimation = (props: ISignatureAnimationProps) => {
  const { className } = props;
  const { isMobile } = useBreakpoint();
  const dotLottieRef = React.useRef<DotLottie | null>(null);
  const frameListenerRef = React.useRef<((event: { currentFrame: number }) => void) | null>(null);
  const completeListenerRef = React.useRef<(() => void) | null>(null);
  const [windowWidth, setWindowWidth] = React.useState(MOBILE_BREAKPOINT);

  React.useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      React.startTransition(() => {
        setWindowWidth(w);
      });
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const scale =
    isMobile && windowWidth < MOBILE_BREAKPOINT
      ? windowWidth / MOBILE_BREAKPOINT
      : 1;

  const elementHeight = Math.round((isMobile ? 70 : 82) * scale);
  const visibleHeight = Math.round((isMobile ? 170 : 200) * scale);
  const canvasWidth = Math.round(visibleHeight * ANIMATION_ASPECT_RATIO);
  const marginTop = -((visibleHeight - elementHeight) / 2);

  const freezeOnLastFrame = React.useCallback(() => {
    const currentAnimation = dotLottieRef.current;
    if (!currentAnimation) {
      return;
    }

    const lastFrame = Math.max(0, currentAnimation.totalFrames - 2);
    currentAnimation.setFrame(lastFrame);
    currentAnimation.pause();
  }, []);

  React.useEffect(() => {
    return () => {
      const currentAnimation = dotLottieRef.current;

      if (currentAnimation && frameListenerRef.current) {
        currentAnimation.removeEventListener("frame", frameListenerRef.current);
      }

      if (currentAnimation && completeListenerRef.current) {
        currentAnimation.removeEventListener("complete", completeListenerRef.current);
      }
    };
  }, []);

  return (
    <StyledDiv
      styles={{
        position: "relative",
        height: elementHeight,
        display: "inline-flex",
        alignItems: "center",
        overflow: "visible",
      }}
    >
      <StyledDiv
        className={clsx(className, "signature-animation")}
        styles={{
          height: visibleHeight,
          marginTop,
          marginBottom: marginTop,
          display: "inline-flex",
          position: "relative",
          overflow: "visible",
        }}
      >
        <DotLottieReact
          data={signatureAnimationData}
          autoplay
          loop={false}
          dotLottieRefCallback={(ref) => {
            const previousAnimation = dotLottieRef.current;
            if (previousAnimation && frameListenerRef.current) {
              previousAnimation.removeEventListener("frame", frameListenerRef.current);
            }
            if (previousAnimation && completeListenerRef.current) {
              previousAnimation.removeEventListener(
                "complete",
                completeListenerRef.current
              );
            }

            dotLottieRef.current = ref;

            if (!ref) {
              return;
            }

            frameListenerRef.current = ({ currentFrame }) => {
              if (currentFrame >= ref.totalFrames - 2) {
                freezeOnLastFrame();
              }
            };
            completeListenerRef.current = () => freezeOnLastFrame();

            ref.addEventListener("frame", frameListenerRef.current);
            ref.addEventListener("complete", completeListenerRef.current);
          }}
          style={{ width: canvasWidth, height: visibleHeight }}
          layout={{
            fit: "contain",
          }}
        />
      </StyledDiv>
    </StyledDiv>
  );
};

export default SignatureAnimation;
